import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { wsManager } from '../api/websocket';

/**
 * Глобальные браузерные уведомления по алертам: toast виден только на открытой
 * странице «Алерты», а когда вкладка в фоне — не виден вовсе. Notification API
 * + короткий beep дают алерт без Telegram-roundtrip'а. Ничего не рендерит.
 */
function beep() {
  // Выключается в колокольчике (ключ общий) — звук на каждый алерт при
  // активном сканере утомляет быстрее, чем приносит пользы.
  try { if (localStorage.getItem('sb_alert_sound') === '0') return; } catch { /* ignore */ }
  try {
    const Ctx = window.AudioContext
      || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
    osc.onended = () => { void ctx.close(); };
  } catch { /* нет звука — не страшно */ }
}

function fireSummary(fire: Record<string, unknown>): string {
  const f = fire as Record<string, string | number | undefined>;
  const sym = f.symbol ?? '';
  switch (f.kind) {
    case 'price':        return `${sym} @ ${f.exchange}: $${Number(f.price).toFixed(4)}`;
    case 'liquidation':  return i18n.t('alertNotifications:liquidation', { sym, exchange: String(f.exchange), pct: Number(f.distance_pct ?? 0).toFixed(2) });
    case 'funding_flip': return i18n.t('alertNotifications:fundingFlip', { sym, exchange: String(f.exchange) });
    case 'funding_soon': return i18n.t('alertNotifications:fundingSoon', { sym, exchange: String(f.exchange), mins: Number(f.mins_left ?? 0).toFixed(0) });
    case 'net_edge':     return `${sym} ${f.short_exchange}→${f.long_exchange}: net edge ${Number(f.net_edge ?? 0).toFixed(3)}%`;
    default:             return `${sym} ${f.short_exchange ?? ''}→${f.long_exchange ?? ''}: ${Number(f.spread_pct ?? 0).toFixed(3)}%`;
  }
}

export const AlertNotifications: React.FC = () => {
  const { t } = useTranslation('alertNotifications');
  useEffect(() => {
    // Разрешение просим один раз; отказ уважаем (больше не спрашиваем).
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      void Notification.requestPermission().catch(() => {});
    }
    wsManager.subscribeChannels(['alerts']);
    const unsub = wsManager.subscribe('alert', (msg: unknown) => {
      const fire = (msg as { data?: Record<string, unknown> })?.data;
      if (!fire || !fire.ts) return;
      beep();
      // Тост показывали только на странице «Алерты» — на остальных вкладках
      // срабатывание проходило незамеченным. Компонент смонтирован глобально
      // (AppShell), так что здесь ему и место.
      toast.success(`🔔 ${fireSummary(fire)}`, { duration: 6000, id: `alert-${fire.ts}` });
      // Системная нотификация — только когда вкладка НЕ на переднем плане
      // (на переднем видно toast на странице алертов).
      if (document.hidden && typeof Notification !== 'undefined'
          && Notification.permission === 'granted') {
        try {
          const n = new Notification(t('title'), {
            body: fireSummary(fire),
            tag: `alert-${fire.alert_id ?? ''}`,   // схлопывает спам одного правила
          });
          n.onclick = () => { window.focus(); n.close(); };
        } catch { /* Notification может кинуть в непривилегированном контексте */ }
      }
    });
    return () => { wsManager.unsubscribeChannels(['alerts']); unsub?.(); };
  }, []);
  return null;
};
